import React from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, Lightbulb, Loader, RefreshCw } from "lucide-react";
import { useRebalanceSuggestions } from "../hooks/useTreasury";
import { useCollapsed } from "../hooks/useCollapsed";
import { formatCurrencyAmount } from "../utils/formatCurrencyAmount";

const fmt = (n) =>
  Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Suggestions only - nothing here moves money. "Open in console" pre-fills the
// Partner Conversion console; the real partner quote and an explicit confirm
// (plus a second admin's approval above the USD limit) still apply there.
function SuggestedActions() {
  const { data, isLoading, isFetching, isError, refetch } = useRebalanceSuggestions();
  const { collapsed, toggle } = useCollapsed("treasury-suggested-actions");

  const header = (
    <div className={`flex gap-2 items-center text-sm font-bold text-[#013941] ${collapsed ? "" : "mb-3"}`}>
      <button
        onClick={toggle}
        className="flex gap-2 items-center font-bold"
        aria-expanded={!collapsed}
        title={collapsed ? "Show suggested actions" : "Hide suggested actions"}
      >
        {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        <Lightbulb className="w-4 h-4" />
        Suggested actions
        {collapsed && data && (data.suggestions?.length > 0 || data.unresolved?.length > 0) && (
          <span className="px-2 py-0.5 text-xs font-semibold text-amber-800 bg-amber-100 rounded-full">
            {(data.suggestions?.length ?? 0) + (data.unresolved?.length ?? 0)}
          </span>
        )}
      </button>
      {data?.computedAt && !isFetching && (
        <span className="text-xs font-normal text-gray-400">
          calculated {new Date(data.computedAt).toLocaleTimeString()}
        </span>
      )}
      <button
        onClick={() => refetch()}
        disabled={isFetching}
        className="flex gap-1 items-center ml-auto text-xs font-medium text-[#013941] hover:underline disabled:opacity-50"
        title="Recalculate from today's position"
      >
        <RefreshCw className={`w-3 h-3 ${isFetching ? "animate-spin" : ""}`} />
        Refresh
      </button>
    </div>
  );

  // Collapsed: just the header (with a count badge), whatever the state.
  if (collapsed) {
    return <div className="p-3 mb-4 rounded border border-gray-200 bg-gray-50/50">{header}</div>;
  }

  // Always visible while it works, so it is clear something is happening
  // (it used to render nothing until the result arrived).
  if (isLoading) {
    return (
      <div className="p-4 mb-4 rounded border border-gray-200 bg-gray-50/50">
        {header}
        <p className="flex gap-2 items-center text-sm text-gray-500">
          <Loader className="w-4 h-4 animate-spin" /> Calculating suggestions from today's position...
        </p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-4 mb-4 rounded border border-gray-200 bg-gray-50/50">
        {header}
        <p className="text-sm text-red-600">Could not calculate suggestions. Try Refresh.</p>
      </div>
    );
  }

  if (!data) return null;
  const { suggestions = [], unresolved = [], note } = data;

  if (suggestions.length === 0 && unresolved.length === 0 && !note) {
    return (
      <div className="p-4 mb-4 rounded border border-gray-200 bg-gray-50/50">
        {header}
        <p className="text-sm text-gray-500">Nothing to rebalance. Every currency is covered with its buffer.</p>
      </div>
    );
  }

  return (
    <div className="p-4 mb-4 rounded border border-gray-200 bg-gray-50/50">
      {header}

      {note && <p className="text-sm text-gray-500">{note}</p>}

      {suggestions.length > 0 && (
        <ul className="space-y-2">
          {suggestions.map((s, i) => {
            const params = new URLSearchParams({
              partner: s.partner,
              from: s.fromCurrency,
              to: s.toCurrency,
              amount: String(s.amount),
            });
            return (
              <li key={`${s.partner}-${s.fromCurrency}-${s.toCurrency}-${i}`} className="flex flex-wrap gap-x-3 gap-y-1 items-center text-sm">
                <span
                  className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                    s.status === "red" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"
                  }`}
                >
                  {s.status === "red" ? "Shortfall" : "Low buffer"}
                </span>
                <span className="text-gray-800">
                  Convert <strong>{formatCurrencyAmount(s.amount, s.fromCurrency)} {s.fromCurrency}</strong> → {s.toCurrency} at{" "}
                  <strong>{s.partner}</strong>
                </span>
                <span className="text-gray-500">
                  (≈ {formatCurrencyAmount(s.indicativeToAmount, s.toCurrency)} {s.toCurrency}
                  {s.indicativeToAmountUsd !== null ? `, ≈ $${fmt(s.indicativeToAmountUsd)}` : ""})
                </span>
                <Link
                  to={`/treasury/partner-conversion?${params.toString()}`}
                  className="font-medium text-[#013941] hover:underline"
                >
                  Open in console
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      {unresolved.length > 0 && (
        <div className="mt-3">
          <div className="mb-1 text-xs font-bold tracking-wider text-gray-500 uppercase">Needs a top-up</div>
          <ul className="space-y-1">
            {unresolved.map((u) => (
              <li key={u.currency} className="text-sm text-gray-700">
                <strong>{u.currency}</strong>: still short by {formatCurrencyAmount(u.remainingShortfall, u.currency)}
                {u.remainingShortfallUsd !== null ? ` (≈ $${fmt(u.remainingShortfallUsd)})` : ""} - no conversion
                partner holds enough surplus elsewhere.
                {u.inTransitAmount ? (
                  <span className="text-emerald-700">
                    {" "}
                    {formatCurrencyAmount(u.inTransitAmount, u.currency)} {u.currency} is on its way
                    {u.inTransitExpectedAt ? ` (ETA ${u.inTransitExpectedAt})` : ""} - not counted as held yet.
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default SuggestedActions;
